<template>
  <div class="calendar-bar">
    <div class="calendar-bar__label">
      <i class="far fa-calendar-alt mr-50"></i>
      <span>{{ $t('Date range') }}</span>
    </div>

    <div class="calendar-bar__pills">
      <button
          v-for="option in options"
          :key="option"
          type="button"
          class="calendar-bar__pill"
          :class="{ 'calendar-bar__pill--active': selected === option }"
          @click="onOptionSelected(option)"
      >
        <span class="calendar-bar__pill-label">{{ $t(`filters.picker.${option}`) }}</span>
      </button>
    </div>

    <!-- Custom range — the same range picker used by the client filters.
         Click a field to open the calendar (handles its own positioning).
         The fields always MIRROR the active preset's resolved dates
         (e.g. "Yesterday" → 08/07/2026 → 08/07/2026), so the actual range is
         visible at all times; editing them switches to Custom mode. -->
    <div class="calendar-bar__custom" :class="{ 'calendar-bar__custom--active': selected === 'custom' }">
      <span class="calendar-bar__custom-label">{{ $t('filters.picker.range') }}:</span>
      <calendar-picker-range
          v-model="customValue"
          :need-footer="false"
          :clearable="false"
          size="sm"
      />
    </div>
  </div>
</template>


<script>
import CalendarPickerRange from '@/components/widget/picker/calendarPickerRange';

export default {
  name: 'CalendarBar',
  components: { CalendarPickerRange },
  data: () => ({
    options: [
      'all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'last7Days', 'thisMonth', 'lastMonth', 'last30Days', 'thisYear'
    ],
    selected: 'thisMonth',
    customValue: {
      start: null,
      end: null
    },
    // True while the code mirrors a preset's dates into the custom picker —
    // the watcher must not treat that as a manual "custom" selection.
    syncingCustom: false,
  }),
  props: {
    value: {
      default: null
    },
    defaultOption: {
      type: String,
      default: 'thisMonth'
    },
    // Parent drives the range explicitly (insight modals): on mount the bar
    // REFLECTS the v-model value instead of restoring the saved page
    // selection, emits nothing, and never touches that storage — otherwise a
    // modal bar would hijack / get hijacked by the page-level bar, showing a
    // different range than the data was fetched with.
    syncWithValue: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    // syncWithValue mode: keep the pills/fields mirroring the parent's range
    // (covers a re-shown modal passing a new range). Regular mode keeps the
    // one-way flow it always had.
    value: {
      deep: true,
      handler() {
        if (this.syncWithValue) {
          this.initFromValue();
        }
      },
    },
    customValue: {
      handler(val) {
        if (this.syncingCustom) return;
        // Emit only once a full start–end range is chosen.
        if (val && val.start && val.end) {
          // The picker normalizes and re-emits mirrored preset dates (e.g. on
          // mount) — ignore those echoes so the active preset pill stays
          // selected; only a genuinely different range switches to Custom.
          if (this.selected !== 'custom') {
            const v = this.getValue(this.selected);
            if (v && v.from === val.start && v.until === val.end) return;
          }
          this.selected = 'custom';
          this.saveSelection({ selected: 'custom', range: { start: val.start, end: val.end } });
          this.$emit('input', { from: val.start, until: val.end });
        }
      },
      deep: true,
    },
  },
  created() {
    if (this.syncWithValue) {
      this.initFromValue();
      return;
    }
    // Restore the user's last date selection for this view (so navigating
    // away and back keeps the range instead of resetting to the default).
    const saved = this.loadSelection();
    if (saved && saved.selected) {
      this.selected = saved.selected;
      if (saved.selected === 'custom' && saved.range && saved.range.start && saved.range.end) {
        this.syncCustomDisplay({ from: saved.range.start, until: saved.range.end });
        this.$emit('input', { from: saved.range.start, until: saved.range.end });
        return;
      }
      const value = this.getValue(this.selected);
      this.syncCustomDisplay(this.selected === 'all' ? null : value);
      this.$emit('input', value);
      return;
    }
    if (this.options.includes(this.defaultOption)) {
      this.selected = this.defaultOption;
    }
    this.onOptionSelected(this.selected);
  },
  methods: {
    /** Mirror the parent's {from, until} into the pills + fields, emitting nothing. */
    initFromValue() {
      const v = this.value;
      if (v && v.from && v.until) {
        // Highlight the preset those exact dates resolve to (if any), else Custom.
        const preset = this.options.find(o => {
          const r = this.getValue(o);
          return r && r.from === v.from && r.until === v.until;
        });
        this.selected = preset || 'custom';
        this.syncCustomDisplay(v);
      } else {
        // No range = lifetime, which is what the "all" pill means.
        this.selected = 'all';
        this.syncCustomDisplay(null);
      }
    },
    storageKey() {
      const name = this.$route && this.$route.name ? this.$route.name : 'global';
      // v2: default preset changed to thisMonth — old saved selections must not override it.
      return `${name}-calendar-range-v2`;
    },
    saveSelection(payload) {
      if (this.syncWithValue) return; // never overwrite the page bar's saved range
      try {
        sessionStorage.setItem(this.storageKey(), JSON.stringify(payload));
      } catch (e) { /* ignore quota / disabled storage */ }
    },
    loadSelection() {
      try {
        return JSON.parse(sessionStorage.getItem(this.storageKey()));
      } catch (e) {
        return null;
      }
    },
    /**
     * Public: parent resets the bar (e.g. when the page filters are reset).
     * Pass an option name ('all', 'thisWeek', …) to land on it; defaults to
     * the bar's default preset.
     */
    reset(option) {
      if (this.syncWithValue) {
        this.initFromValue();
        return;
      }
      try { sessionStorage.removeItem(this.storageKey()); } catch (e) { /* ignore */ }
      const target = option && this.options.includes(option) ? option
          : (this.options.includes(this.defaultOption) ? this.defaultOption : this.selected);
      this.onOptionSelected(target);
    },
    onOptionSelected(option) {
      this.selected = option;
      this.saveSelection({ selected: option });
      const value = this.getValue(option);
      // Mirror the resolved dates into the custom picker so the ACTUAL range
      // behind the preset ("yesterday" → 08/07/2026) is always visible and
      // stays synchronized. "All" has no real bounds → clear the fields.
      this.syncCustomDisplay(option === 'all' ? null : value);
      this.$emit('input', value);
    },
    /** Show the given {from, until} in the custom picker WITHOUT triggering the custom-mode watcher. */
    syncCustomDisplay(value) {
      this.syncingCustom = true;
      this.customValue = value && value.from && value.until
          ? { start: value.from, end: value.until }
          : { start: null, end: null };
      this.$nextTick(() => {
        this.syncingCustom = false;
      });
    },
    getValue(option) {
      switch (option) {
        case 'all':
          return { from: this.$moment().subtract(30, 'years').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'today':
          return { from: this.$moment().format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'yesterday':
          return { from: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'days').format('YYYY-MM-DD') };
        case 'thisWeek':
          return { from: this.$moment().startOf('week').format('YYYY-MM-DD'), until: this.$moment().endOf('week').format('YYYY-MM-DD') };
        case 'lastWeek':
          return { from: this.$moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD') };
        case 'last7Days':
          return { from: this.$moment().subtract(7, 'days').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'thisMonth':
          return { from: this.$moment().startOf('month').format('YYYY-MM-DD'), until: this.$moment().endOf('month').format('YYYY-MM-DD') };
        case 'lastMonth':
          return { from: this.$moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') };
        case 'last30Days':
          return { from: this.$moment().subtract(30, 'days').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'thisYear':
          return { from: this.$moment().startOf('year').format('YYYY-MM-DD'), until: this.$moment().endOf('year').format('YYYY-MM-DD') };
      }
    },
  }
};
</script>


<style lang="scss" scoped>
.calendar-bar {
  position: relative;
  z-index: 5; // keep the calendar dropdown above the dashboard cards below
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(4px);

  &__label {
    display: flex;
    align-items: center;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;

    i { color: #007BFF; }
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    min-width: 0;
  }

  &__pill {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    transition: all 0.18s ease;
    white-space: nowrap;

    &:hover { background: rgba(0, 123, 255, 0.08); color: #007BFF; }
    &:focus-visible { box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25); }

    &--active {
      color: #ffffff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);

      &:hover { color: #ffffff; background: linear-gradient(135deg, #66b5ff 0%, #1a8cff 60%, #0066cc 100%); }
    }
  }

  // Custom range picker (start → end fields) — always mirrors the active
  // preset's resolved dates so the real range is visible.
  &__custom {
    margin-left: auto;
    flex: 0 1 340px;
    min-width: 260px;
    display: flex;
    align-items: center;
    gap: 0.45rem;

    &-label {
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6e6b7b;
      white-space: nowrap;
      flex-shrink: 0;
    }

    ::v-deep .date-picker-range-container {
      flex: 1 1 auto;
      min-width: 0;

      .form-control {
        background: #ffffff;
        border-color: rgba(110, 117, 159, 0.22);
        color: #2f2b50;
        font-weight: 600;
        font-size: 0.78rem;
        height: 34px;
      }
      .input-group-text {
        background: #ffffff;
        border-color: rgba(110, 117, 159, 0.22);
        color: #007BFF;
      }
      .input-group:first-child .input-group-text { border-radius: 9px 0 0 9px; }
      .date-picker-arrow { color: #b0b6c8; margin: 0 5px; }
    }

    &--active ::v-deep .date-picker-range-container .form-control,
    &--active ::v-deep .date-picker-range-container .input-group-text {
      border-color: rgba(0, 123, 255, 0.5);
    }
  }
}

// Dark theme
.dark-layout .calendar-bar {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  &__label {
    color: #b4b7bd;
    border-right-color: rgba(255, 255, 255, 0.08);
    i { color: #4da3ff; }
  }

  &__pill {
    color: #b4b7bd;
    &:hover { background: rgba(77, 163, 255, 0.15); color: #b3d7ff; }
  }

  &__custom-label { color: #b4b7bd; }

  &__custom ::v-deep .date-picker-range-container {
    .form-control { background: #1a2130; border-color: rgba(255, 255, 255, 0.08); color: #d0d2d6; }
    .input-group-text { background: #1a2130; border-color: rgba(255, 255, 255, 0.08); color: #8fc1ff; }
  }
}

@media (max-width: 768px) {
  .calendar-bar {
    gap: 0.4rem;
    padding: 0.5rem 0.6rem;

    &__label { display: none; }
    &__pill { padding: 0.35rem 0.65rem; font-size: 0.74rem; }
    &__custom { margin-left: 0; flex: 1 1 100%; }
  }
}
</style>

<!-- Global: the v-calendar popover renders with a low z-index, so it can be
     painted behind cards/panels. Lift it above everything. -->
<style lang="scss">
.vc-popover-content-wrapper {
  z-index: 1010 !important;
}
</style>
